import { Fragment, ReactElement } from 'react';
import {
  IDeaInfo,
  ISaveRequestOutput,
  ValueLabel,
  ExpandButton,
  IRequestDetails,
  sailpointApproveRequestUrl,
  formatDate,
  isRequestNotCancellable,
  variables,
} from '../common';
import { Container, Checkbox, Button, styled } from '@mui/material';
import {
  IMoreRequestData,
  RequestStatusType,
  isRequestStatusTypeForApproval,
} from '.';
import {
  map as lmap,
  compose as lcompose,
  compact as lcompact,
  find as lfind,
  replace as lreplace,
  isEmpty as lisEmpty,
} from 'lodash/fp';
import { path, not } from 'rambda';
import { left, right, fold } from 'fp-ts/Either';
import { flow } from 'fp-ts/function';

export const render =
  ({ requestStatusType }: { requestStatusType: RequestStatusType }) =>
  (s: any) =>
  (properties: ValueLabel<keyof ISaveRequestOutput>[]) =>
  ({
    disableOperationsHelpDsk,
    expandRequestValue,
    toggleExpandRequest,
    now,
    selectedRequests,
    toggleRequestSelection,
    confirmCancelRequests,
  }: {
    disableOperationsHelpDsk: boolean;
    expandRequestValue: (name: string) => boolean;
    toggleExpandRequest: (name: string) => void;
    now?: Date;
    selectedRequests?: ISaveRequestOutput[];
    toggleRequestSelection?: (payload: ISaveRequestOutput) => void;
    confirmCancelRequests?: () => void;
  }) =>
  (moreRequestData: IMoreRequestData[]) => {
    const A = isRequestStatusTypeForApproval(requestStatusType)
      ? ApprovalArticle
      : requestStatusType === 'MemberRequestStatus'
      ? MemberRequestStatusArticle
      : Article;

    return lcompose(
      fold(
        () => <Container maxWidth="lg">Empty</Container>,

        requests => (
          <Container maxWidth="xl" style={{ marginTop: '1em' }}>
            <A>
              <h4 key="action">
                {toggleRequestSelection === void 0 ? (
                  `Action`
                ) : (
                  <Button
                    onClick={confirmCancelRequests}
                    disabled={
                      lisEmpty(selectedRequests) || disableOperationsHelpDsk
                    }
                    variant="contained">
                    Cancel Request
                  </Button>
                )}
              </h4>

              {isRequestStatusTypeForApproval(requestStatusType) && (
                <h4>Review/Approve/Reject Link</h4>
              )}

              {lmap<ValueLabel<string>, ReactElement>(({ label }) => (
                <h4 key={label}>{label}</h4>
              ))(properties)}

              {lmap<ISaveRequestOutput, ReactElement>(
                lcompose(
                  ([a, b]) => <Fragment key={a.requestId}>{b}</Fragment>,
                  ([a, b]) => [a, lcompact(b)],
                  a => [
                    a,
                    [
                      <span key="expand">
                        {(requestStatusType === 'SelfRequestStatus' ||
                          requestStatusType === 'MemberRequestStatus') && (
                          <Checkbox
                            checked={flow(
                              lfind<ISaveRequestOutput>({
                                requestId: a.requestId,
                              }),
                              lisEmpty,
                              not
                            )(selectedRequests)}
                            disabled={isRequestNotCancellable(a)}
                            color="primary"
                            onClick={toggleRequestSelection?.bind(null, a)}
                          />
                        )}
                        <ExpandButton
                          value={expandRequestValue(String(a.requestId))}
                          matchesGridBreakpoint
                          onClick={() =>
                            toggleExpandRequest(String(a.requestId))
                          }
                        />
                      </span>,

                      isRequestStatusTypeForApproval(requestStatusType) && (
                        <span key="approve">
                          {disableOperationsHelpDsk ? null : (
                            <Approve
                              className={s.approve}
                              href={sailpointApproveRequestUrl(
                                a.sailpointCaseId
                              )}
                              target="_blank"
                              rel="noreferrer">
                              Review/Approve/Reject
                            </Approve>
                          )}
                        </span>
                      ),

                      ...lmap<
                        ValueLabel<keyof ISaveRequestOutput>,
                        ReactElement | undefined
                      >(({ value }) => (
                        <SpanPV
                          key={`${a.requestId}-${value}`}
                          className={s.properyValue}>
                          {show(a)(value)}
                        </SpanPV>
                      ))(properties),

                      expandRequestValue(String(a.requestId)) ? (
                        <span key="deanum" style={{ gridColumn: '2/-1' }}>
                          DEA Registration Numbers:{' '}
                          {showDetails(
                            lcompose(
                              path<IRequestDetails>('details'),
                              lfind<IMoreRequestData>({
                                requestId: a.requestId,
                              })
                            )(moreRequestData)
                          )('deaInfos')}
                        </span>
                      ) : (
                        void 0
                      ),
                    ],
                  ]
                )
              )(requests)}
            </A>
            {/* <pre>{JSON.stringify(selectedRequests, null, 2)}</pre> */}
          </Container>
        )
      ),

      (requests: ISaveRequestOutput[]) =>
        requests.length === 0 ? left(requests) : right(requests)
    );
  };

export const show =
  (a: ISaveRequestOutput) =>
  (value: keyof ISaveRequestOutput): string | ReactElement[] | undefined => {
    if (value === 'deaInfos') {
      return lmap<IDeaInfo, ReactElement>(b => (
        <span key={`${b.deaNum}-deaNum`}>{b.deaNum} </span>
      ))(a[value]);
    }

    if (value === 'updatedAt') {
      return formatDate(a[value]);
    }

    if (value === 'beneficiaryInfo') {
      return [
        <div key="beneficiaryInfo-1">{`${
          a.beneficiaryFirstName || a.firstName
        } ${a.beneficiaryLastName || a.lastName}`}</div>,
        <div key="beneficiaryInfo-2">{`${a.beneficiaryEmail}`}</div>,
      ];
    }

    if (value === 'requestorEmail') {
      if (a.requestorEmail === 'RA Group') {
        return [<div key="requestorEmail-1">{`Registration Authority`}</div>];
      }
      if (a.requestorEmail === 'DI Group') {
        return [<div key="requestorEmail-1">{`Diversion Investigators`}</div>];
      }
      if (a.requestorEmail === 'Drug Enforcement Administrator') {
        return [<div key="requestorEmail-1">{`Drug Enforcement Administrator`}</div>];
      }
      return [
        <div key="requestorEmail-1">{`${a.requestorFirstName || ''} ${
          a.requestorLastName || ''
        }`}</div>,
        <div key="requestorEmail-2">{`${a.requestorEmail}`}</div>,
      ];
    }

    if (value === 'status') {
      if (a.status === 'FAILURE') {
        return 'PENDING';
      }
      return lreplace(/_/g, ' ')(a[value]);
    }

    if (value === 'additionalInfo' && a.status === 'FAILURE') {
      return 'Your account has not been fully synchronized. Please create CSOS Certificate request in 2-3 hours.';
    }

    return a[value] as string;
  };

const showDetails =
  (a: IRequestDetails | undefined) =>
  (value: keyof IRequestDetails): string | ReactElement[] => {
    if (a === undefined) {
      return '';
    }

    if (value === 'deaInfos') {
      return lmap<IDeaInfo, ReactElement>(b => (
        <span key={`${b.deaNum}-deaNum`}>{b.deaNum} </span>
      ))(a[value]);
    }

    return a[value] as string;
  };

const ApprovalArticle = styled('article')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  '&>*+*': {
    marginTop: '1rem',
  },

  '@media (min-width: 60em)': {
    display: 'grid',
    gridTemplateColumns: 'auto 160px repeat(3, auto)  repeat(3, auto)',
    gap: '1px',

    backgroundColor: variables.grayLighter,
    border: variables.cardBorder,
    borderRadius: variables.cardBorderRadius,

    '&>*': {
      backgroundColor: '#fff',
      padding: '1rem',
    },

    '&>*+*': {
      marginTop: '0rem',
    },
  },
}));

const MemberRequestStatusArticle = styled('article')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  '&>*+*': {
    marginTop: '1rem',
  },

  '@media (min-width: 60em)': {
    display: 'grid',
    gridTemplateColumns: 'repeat(5, auto) 1fr repeat(3, auto)',
    gap: '1px',

    backgroundColor: variables.grayLighter,
    border: variables.cardBorder,
    borderRadius: variables.cardBorderRadius,

    '&>*': {
      backgroundColor: '#fff',
      padding: '1rem',
    },

    '&>*+*': {
      marginTop: '0rem',
    },
  },
}));

const Article = styled('article')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',

  '&>*+*': {
    marginTop: '1rem',
  },

  '@media (min-width: 60em)': {
    display: 'grid',
    gridTemplateColumns: 'repeat(6, auto) 1fr 2fr 2fr auto',
    gap: '1px',

    backgroundColor: variables.grayLighter,
    border: variables.cardBorder,
    borderRadius: variables.cardBorderRadius,

    '&>*': {
      backgroundColor: '#fff',
      padding: '1rem',
    },

    '&>*+*': {
      marginTop: '0rem',
    },
  },
}));

const Approve = styled('a')(({ theme }) => ({
  color: variables.aLink,
  '&:focus,&:hover,&:active': {
    outline: '1px solid currentColor',
    outlineOffset: '1px',
    fontSize: '1em',
  },
}));

const SpanPV = styled('span')(({ theme }) => ({
  properyValue: {
    overflow: 'auto',
  },
}));
